import React, { useState, useMemo } from 'react';
import { Check, Trash2, Inbox, Mail, ChevronDown, Paperclip } from 'lucide-react';
import { ActionPill, EmptyState, StatusBadge } from '../../../ui';
import { useQueueItems } from '../useQueueItems';

const TIER_TO_PRIORITY = { A: 'high', B: 'medium', C: 'low' };

// ── Task queue item card (shared with KanbanView and CalendarView) ──────────
function TaskQueueCard({ item, actions }) {
  const priority = item.priority || TIER_TO_PRIORITY[item.criticalityTier] || 'medium';
  const priorityColor = priority === 'high' || priority === 'critical' ? 'var(--red-critical)' : priority === 'medium' ? 'var(--yellow)' : 'var(--green)';
  // For email-sourced items, pull subject/sender from sourceData or emailData
  const source = item.sourceData || item.emailData || {};
  const title = item.title || item.taskName || item.name || source.subject || 'Queue Item';
  const subtitle = item.category || item.domain || source.domain || '';
  const dueLabel = item.dueDate ? ` · Due ${item.dueDate.slice(0, 10)}` : '';

  return (
    <div className="rounded-lg border border-nn-border bg-nn-white p-2.5 flex items-center gap-3" style={{ borderLeft: `3px solid ${priorityColor}` }}>
      <Inbox size={14} className="shrink-0" style={{ color: 'var(--aqua)' }} />
      <div className="flex-1 min-w-0">
        <div className="text-body font-medium text-text-dark truncate">{title}</div>
        <div className="text-caption text-text-muted truncate">{subtitle}{dueLabel}</div>
        {source.sender && <div className="text-caption text-text-muted truncate">From: {source.sender}</div>}
      </div>
      <StatusBadge variant={priority === 'high' || priority === 'critical' ? 'critical' : priority === 'medium' ? 'watch' : 'on-track'}>
        {priority}
      </StatusBadge>
      {actions && <TaskItemActions item={item} onComplete={actions.completeItem} onRemove={actions.removeItem} />}
    </div>
  );
}

// ── Action buttons for task items ────────────────────────────────────────────
function TaskItemActions({ item, onComplete, onRemove }) {
  return (
    <ActionPill>
      <ActionPill.Button tip="Complete" color="var(--green)" onClick={() => onComplete(item.id)}>
        <Check size={12} />
      </ActionPill.Button>
      <ActionPill.Button tip="Remove" color="var(--red-critical)" onClick={() => onRemove(item.id)}>
        <Trash2 size={12} />
      </ActionPill.Button>
    </ActionPill>
  );
}

// ── Email topic card — expandable thread view ─────────────────────────────
function EmailTaskCard({ item, actions }) {
  const [expanded, setExpanded] = useState(false);
  const ctx = item.threadContext || item.sourceData || {};
  const emails = ctx.emails || [];
  const priority = item.priority || (ctx.urgency >= 4 ? 'high' : ctx.urgency >= 3 ? 'medium' : 'low');
  const priorityColor = priority === 'high' || priority === 'critical' ? 'var(--red-critical)' : priority === 'medium' ? 'var(--yellow)' : 'var(--green)';
  const participants = ctx.participants || [];
  const msgCount = ctx.messageCount || emails.length || 1;
  const attachCount = ctx.attachmentCount || 0;

  return (
    <div className="rounded-lg border border-nn-border bg-nn-white overflow-hidden" style={{ borderLeft: `3px solid ${priorityColor}` }}>
      {/* Collapsed header — always visible */}
      <div className="p-2.5 flex items-start gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <Mail size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--royal)' }} />
        <div className="flex-1 min-w-0">
          <div className="text-body font-medium text-text-dark truncate">{item.title || ctx.topicName || 'Email Topic'}</div>
          <div className="text-caption text-text-muted truncate">
            {participants.join(', ')}{participants.length > 0 ? ' · ' : ''}{msgCount} email{msgCount !== 1 ? 's' : ''}
            {attachCount > 0 && ` · ${attachCount} attachment${attachCount !== 1 ? 's' : ''}`}
            {item.dueDate ? ` · Due ${item.dueDate.slice(0, 10)}` : ''}
          </div>
          {!expanded && ctx.latestPreview && (
            <div className="text-caption text-text-muted truncate mt-0.5 italic">&quot;{ctx.latestPreview.slice(0, 80)}...&quot;</div>
          )}
        </div>
        <StatusBadge variant={priority === 'high' || priority === 'critical' ? 'critical' : priority === 'medium' ? 'watch' : 'on-track'}>
          {priority}
        </StatusBadge>
        <ChevronDown size={14} className={`shrink-0 text-text-muted transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {/* Expanded: show all emails */}
      {expanded && (
        <div className="border-t border-nn-border">
          {emails.map((email, idx) => (
            <div key={email.id || idx} className={`px-4 py-2.5 ${idx > 0 ? 'border-t border-nn-border' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-body-sm font-semibold text-text-dark">{email.from}</span>
                <span className="text-caption text-text-muted">{email.receivedAt ? new Date(email.receivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
              </div>
              {email.subject && <div className="text-caption text-text-muted">{email.subject}</div>}
              <div className="text-body-sm text-text-dark mt-1">{email.bodyText || email.preview || ''}</div>
              {email.attachments?.length > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Paperclip size={10} className="text-text-muted" />
                  <span className="text-caption text-text-muted">{email.attachments.map(a => a.name).join(', ')}</span>
                </div>
              )}
            </div>
          ))}
          {/* Actions at bottom of expanded card */}
          {actions && (
            <div className="flex justify-end px-4 py-2 border-t border-nn-border bg-surface">
              <TaskItemActions item={item} onComplete={actions.completeItem} onRemove={actions.removeItem} />
            </div>
          )}
        </div>
      )}

      {/* Actions on collapsed card (same as TaskQueueCard) */}
      {!expanded && actions && (
        <div className="flex justify-end px-2.5 pb-2">
          <TaskItemActions item={item} onComplete={actions.completeItem} onRemove={actions.removeItem} />
        </div>
      )}
    </div>
  );
}

function PriorityQueueView() {
  const { todayItems, completeItem, removeItem } = useQueueItems();
  const [filter, setFilter] = useState(null);

  // Normalize all items for priority counting
  const itemsWithPriority = useMemo(() => todayItems.map((item) => ({
    ...item,
    _priority: item.priority || TIER_TO_PRIORITY[item.criticalityTier] || 'medium',
  })), [todayItems]);

  const highCount = itemsWithPriority.filter((i) => i._priority === 'high' || i._priority === 'critical').length;
  const medCount  = itemsWithPriority.filter((i) => i._priority === 'medium').length;
  const lowCount  = itemsWithPriority.filter((i) => i._priority === 'low').length;
  const totalCount = itemsWithPriority.length;

  const displayed = filter
    ? itemsWithPriority.filter((t) => t._priority === filter || (filter === 'high' && t._priority === 'critical'))
    : itemsWithPriority;

  const FILTERS = [
    { key: null,     label: 'TOTAL',  count: totalCount, color: 'var(--text-muted)' },
    { key: 'high',   label: 'HIGH',   count: highCount,  color: 'var(--red-critical)' },
    { key: 'medium', label: 'MEDIUM', count: medCount,   color: 'var(--yellow)' },
    { key: 'low',    label: 'LOW',    count: lowCount,   color: 'var(--green)' },
  ];

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', padding: 8, gap: 8 }}>
      {/* Filter bar */}
      <div className="flex shrink-0 rounded-lg border border-nn-border overflow-hidden">
        {FILTERS.map(({ key, label, count, color }, idx) => {
          const active = filter === key;
          return (
            <button
              key={String(key)}
              type="button"
              onClick={() => setFilter(active ? null : key)}
              className="flex-1 flex items-center gap-1.5 px-3 py-1.5 border-none cursor-pointer transition-colors"
              style={{
                background: active ? 'var(--surface-warm)' : 'var(--white)',
                borderLeft: idx > 0 ? `2px solid ${color}` : undefined,
              }}
            >
              <span className="text-[18px] font-extrabold leading-none" style={{ color }}>{count}</span>
              <span className="text-[10px] font-semibold" style={{ color: active ? color : 'var(--text-dark-secondary)' }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Queue list — task items only */}
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {displayed.length === 0 ? (
          <EmptyState title="No queued items" body="Add items from any page using the queue icon, or queue emails from the inbox." size="sm" />
        ) : (
          displayed.map((item) => {
            const isEmailTopic = item.type === 'email_topic' || item.threadContext?.sourceType === 'email_topic';
            if (isEmailTopic) {
              return <EmailTaskCard key={item.id} item={item} actions={{ completeItem, removeItem }} />;
            }
            return <TaskQueueCard key={item.id} item={item} actions={{ completeItem, removeItem }} />;
          })
        )}
      </div>
    </div>
  );
}

export { PriorityQueueView, TaskQueueCard, EmailTaskCard };
